import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { FormlyJsonschema } from '@ngx-formly/core/json-schema';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import { IsEqualService } from '@services/utilities/1.0/is-equal.service';
import { FormlyModule } from '@ngx-formly/core';
@Component({
  selector: 'txp-json-form-cva',
  templateUrl: './json-form-cva.component.html',
  styleUrls: ['./json-form-cva.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JsonFormCvaComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => JsonFormCvaComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [FormlyModule],
})
export class JsonFormCvaComponent implements ControlValueAccessor, Validator, OnChanges, OnDestroy {
  @Input() template: any;
  @Input() ignoreRequired: boolean;
  @Input() permission: boolean;

  disabled: boolean;
  fields: any;
  fieldsNoRequired: any;
  data: any;
  onTouched: any;
  form: UntypedFormGroup;
  lastValue: any;

  private unsubscribe = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private formlyJsonschema: FormlyJsonschema,
    private ngZone: NgZone,
    private translateService: TranslateService,
  ) {
    this.form = new UntypedFormGroup({});
    this.disabled = this.permission;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.template) {
      if (this.template) {
        // setup the template with required fields removed for use if ignoreRequired is true
        const cloneTemplate = DeepCloneService.clone(this.template);
        cloneTemplate.schema = this.removeRequired(cloneTemplate.schema);
        this.fields = [
          this.formlyJsonschema.toFieldConfig(this.processRequiredFields(this.template.schema)),
        ];
        this.fieldsNoRequired = [this.formlyJsonschema.toFieldConfig(cloneTemplate.schema)];
      } else {
        this.fieldsNoRequired = undefined;
        this.fields = undefined;
      }
      // need to wait for controls to be added to form by formly before we set disabled state
      this.ngZone.onStable.pipe(take(1)).subscribe(() => {
        this.setDisabledState(this.disabled);
      });
    }
    if (changes.permission) {
      this.disabled = this.permission;
    }
  }

  registerOnChange(fn: any): void {
    this.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        // ignore if this value matches the last one
        filter((v) => !IsEqualService.isEqual(v, this.lastValue)),
        tap((v) => {
          this.lastValue = v;
        }),
      )
      .subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    // ignore
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    isDisabled ? this.form.disable() : this.form.enable();
  }

  writeValue(obj: any): void {
    this.data = obj;
    this.lastValue = DeepCloneService.clone(obj);
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.form.valid
      ? undefined
      : {
          invalidForm: {
            valid: false,
            message: this.translateService.instant('form.error.fieldsAreInvalid'),
          },
        };
  }

  /**
   * Formly only uses the required fields array defined at the top of the schema and not on the fields
   * themselves. Fix the schema to work with Formly required fields by adding a top-level "required"
   * property array.
   *
   * @param schema - schema to process
   */
  processRequiredFields(schema: any): any {
    if (schema.properties) {
      const required = [];
      const properties = Object.keys(schema.properties);
      for (const key of properties) {
        const field = schema.properties[key];
        if (field.required === true) {
          required.push(key);
        }
        // fields that are arrays can have an items property with more fields,
        // recurse into those
        if (field.items) {
          this.processRequiredFields(field.items);
        }
      }
      schema.required = required;
    }
    return schema;
  }

  /**
   * Returns a copy of the object with all nested 'required' properties removed
   *
   * @param schema - object or array to remove 'required' from
   * @return copy of the schema with nest 'required' properties removed
   */
  removeRequired(schema: any) {
    let newSchema;
    if (Array.isArray(schema)) {
      newSchema = [];
      for (let i = 0; i < schema.length; i++) {
        newSchema[i] = this.removeRequired(schema[i]);
      }
    } else if (typeof schema === 'object') {
      newSchema = {};
      for (const key in schema) {
        if (schema.hasOwnProperty(key)) {
          if (!(key.toLowerCase() === 'required')) {
            const value = schema[key];
            newSchema[key] = this.removeRequired(value);
          }
        }
      }
    } else {
      // primitive
      newSchema = schema;
    }
    return newSchema;
  }
}
